import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Copy, CheckCircle, IndianRupee, AlertCircle, Gift, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [planDetails, setPlanDetails] = useState(null);
  const [transactionId, setTransactionId] = useState('');
  const [transactionDate, setTransactionDate] = useState('');
  const [amount, setAmount] = useState('');
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [couponApplied, setCouponApplied] = useState(null);

  const API_URL = window.location.origin;
  const token = localStorage.getItem('sessionToken');

  const planId = location.state?.plan_id;
  const planPrice = location.state?.plan_price;
  const planName = location.state?.plan_name;

  useEffect(() => {
    if (!planId) {
      toast.error('Please select a plan first');
      navigate('/select-plan');
      return;
    }
    
    fetchActiveUPI();
    setPlanDetails({
      id: planId,
      price: planPrice,
      name: planName
    });
  }, []);

  const fetchActiveUPI = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payment-settings/active`);
      
      if (response.data && response.data.success) {
        setPaymentInfo(response.data);
        setAmount(planPrice); // Pre-fill amount
      } else {
        toast.error('Payment UPI not configured. Please contact admin.');
        navigate('/select-plan');
      }
    } catch (error) {
      console.error('Error fetching UPI:', error);
      toast.error('Failed to load payment details. Please contact admin.');
      navigate('/select-plan');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUPI = () => {
    navigator.clipboard.writeText(paymentInfo.upi_id);
    setCopied(true);
    toast.success('UPI ID copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setValidatingCoupon(true);
    try {
      // First validate the coupon
      const validateResponse = await axios.post(
        `${API_URL}/api/validate-plan-coupon`,
        { code: couponCode.trim().toUpperCase() },
        { withCredentials: true }
      );

      if (validateResponse.data.success) {
        // For static free coupons (like PICTURAMOVENS100), use the coupon with plan_type
        if (validateResponse.data.coupon_type === 'static_free') {
          // Use the coupon with the selected plan
          const useResponse = await axios.post(
            `${API_URL}/api/use-plan-coupon`,
            { 
              code: couponCode.trim().toUpperCase(),
              plan_type: planId  // Pass the selected plan type
            },
            { withCredentials: true }
          );

          if (useResponse.data.success && useResponse.data.fee_id) {
            // Navigate directly to create-event with auto-approved status
            toast.success(useResponse.data.message || '🎉 Coupon applied! Create your event now.');
            navigate('/create-event', {
              state: {
                fee_id: useResponse.data.fee_id,
                isAutoApproved: true
              }
            });
            return;
          } else if (useResponse.data.needs_plan_selection) {
            // Fallback: redirect to plan selection (shouldn't happen normally)
            toast.success('Coupon valid! Select a plan to apply the discount.');
            navigate('/select-plan');
            return;
          }
        }
        
        // For resume/admin free coupons (existing flow)
        const useResponse = await axios.post(
          `${API_URL}/api/use-plan-coupon`,
          { code: couponCode.trim().toUpperCase() },
          { withCredentials: true }
        );

        if (useResponse.data.success) {
          setCouponApplied({
            code: couponCode.toUpperCase(),
            type: useResponse.data.coupon_type,
            plan_type: useResponse.data.plan_type,
            fee_id: useResponse.data.fee_id
          });
          toast.success(useResponse.data.message || 'Coupon applied successfully!');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired coupon code');
      setCouponCode('');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleProceedWithCoupon = () => {
    if (couponApplied && couponApplied.fee_id) {
      navigate('/create-event', {
        state: {
          fee_id: couponApplied.fee_id,
          isAutoApproved: true
        }
      });
    }
  };

  const handleSubmitPayment = async () => {
    // Validation
    if (!transactionId.trim()) {
      toast.error('Please enter transaction ID');
      return;
    }

    if (!transactionDate) {
      toast.error('Please select transaction date');
      return;
    }

    if (!amount || parseFloat(amount) !== parseFloat(planPrice)) {
      toast.error(`Amount must be exactly ₹${planPrice}`);
      return;
    }

    setSubmitting(true);

    try {
      // Create event fee record
      const response = await axios.post(
        `${API_URL}/api/event-fees`,
        {
          plan_name: planDetails.name,
          amount: parseFloat(amount),
          transaction_id: transactionId.trim(),
          transaction_date: transactionDate,
          payment_method: 'UPI',
          upi_id: paymentInfo.masked_upi
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data && response.data.fee) {
        const feeId = response.data.fee.id;
        
        toast.success('Payment details submitted successfully!');
        toast.info('Waiting for admin verification. You can proceed to create your event.');
        
        // Navigate to event creation with fee_id
        navigate('/create-event', {
          state: {
            fee_id: feeId,
            isAutoApproved: false
          }
        });
      }
    } catch (error) {
      console.error('Error submitting payment:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to submit payment details';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading payment details...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-contribo-navy to-contribo-gold p-4 shadow-xl">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/select-plan')}
            className="text-white hover:bg-white hover:bg-opacity-20 mb-3 text-xs sm:text-sm"
          >
            <ArrowLeft size={16} className="mr-1" />
            Back to Plans
          </Button>
          <div className="flex items-center gap-2">
            <IndianRupee className="text-white flex-shrink-0" size={24} />
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-white">Complete Payment</h1>
              <p className="text-white text-opacity-90 text-xs sm:text-sm mt-1">
                Make payment to proceed with event creation
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-3 py-4 space-y-4">
        {/* Free Coupon Section */}
        <Card className="border-2 border-purple-300 bg-purple-50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-purple-900 text-base sm:text-xl">
              <Gift size={20} className="flex-shrink-0" />
              Have a Free Coupon Code?
            </CardTitle>
          </CardHeader>
          <CardContent>
            {couponApplied ? (
              <div className="bg-green-100 border-2 border-green-400 rounded-lg p-4 text-center">
                <CheckCircle className="mx-auto text-green-600 mb-2" size={40} />
                <p className="font-bold text-green-800 text-lg">Coupon Applied!</p>
                <p className="text-green-700 mt-1">
                  Code: <span className="font-mono font-bold">{couponApplied.code}</span>
                </p>
                <p className="text-green-600 text-sm mt-1">
                  {couponApplied.type === 'free' ? 'Free plan activated!' : 'Resume coupon applied!'} Skip payment and create your event.
                </p>
                <Button
                  onClick={handleProceedWithCoupon}
                  className="mt-4 bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle size={16} className="mr-2" />
                  Proceed to Create Event
                </Button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Enter coupon code (e.g., PLAN-XXXXXX)"
                  className="flex-1 text-sm font-mono uppercase"
                />
                <Button
                  onClick={handleApplyCoupon}
                  disabled={validatingCoupon || !couponCode.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white flex-shrink-0"
                >
                  {validatingCoupon ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    'Apply Coupon'
                  )}
                </Button>
              </div>
            )}
            {!couponApplied && (
              <p className="text-xs text-purple-700 mt-2">
                If you have a free coupon from Contribo, enter it here to skip payment.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Plan Details - Only show if no coupon applied */}
        {!couponApplied && (
          <>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base sm:text-xl">Selected Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-base sm:text-xl font-bold text-gray-800 truncate">{planDetails?.name}</p>
                    <p className="text-xs sm:text-sm text-gray-600">Event Plan</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xl sm:text-3xl font-bold text-contribo-navy">₹{planDetails?.price}</p>
                    <p className="text-xs sm:text-sm text-gray-600">Total Amount</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Instructions */}
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-blue-900 text-base sm:text-xl">
                  <AlertCircle size={20} className="flex-shrink-0" />
              Payment Instructions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 sm:space-y-4">
              {/* Step 1 */}
              <div className="flex gap-2 sm:gap-3">
                <span className="bg-blue-600 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">1</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">Open Your UPI App</p>
                  <p className="text-xs sm:text-sm text-gray-600">Open Google Pay, PhonePe, Paytm, or any UPI app</p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-2 sm:gap-3">
                <span className="bg-blue-600 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">2</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">Pay to Contribo Admin UPI</p>
                  
                  {/* UPI Display */}
                  <div className="bg-white p-3 rounded-lg border-2 border-gray-300 mt-2">
                    <p className="text-xs text-gray-600 mb-1">Pay to:</p>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                      <div className="min-w-0">
                        <p className="text-lg sm:text-2xl font-mono font-bold text-gray-800 break-all">{paymentInfo?.masked_upi}</p>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">{paymentInfo?.upi_name}</p>
                      </div>
                      <Button
                        onClick={handleCopyUPI}
                        size="sm"
                        className="bg-contribo-navy hover:bg-contribo-gold flex-shrink-0 text-xs w-full sm:w-auto"
                      >
                        {copied ? (
                          <>
                            <CheckCircle size={14} className="mr-1" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy size={14} className="mr-1" />
                            Copy UPI
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border-l-4 border-yellow-500 p-2 sm:p-3 rounded mt-2 sm:mt-3">
                    <p className="text-xs text-yellow-800">
                      <strong>⚠️ Important:</strong> Pay exactly <strong>₹{planDetails?.price}</strong> to this UPI ID
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-2 sm:gap-3">
                <span className="bg-blue-600 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">3</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">Note the Transaction Details</p>
                  <p className="text-xs sm:text-sm text-gray-600">After payment, note down your transaction ID and date</p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-2 sm:gap-3">
                <span className="bg-blue-600 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">4</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">Submit Payment Details Below</p>
                  <p className="text-xs sm:text-sm text-gray-600">Fill the form below to complete the process</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Submission Form */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base sm:text-xl">Submit Payment Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 sm:space-y-4">
              <div>
                <Label className="text-sm sm:text-base font-semibold">Transaction ID / UTR Number *</Label>
                <Input
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  placeholder="e.g., 123456789012"
                  className="mt-1 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Find this in your UPI app under transaction history
                </p>
              </div>

              <div>
                <Label className="text-sm sm:text-base font-semibold">Transaction Date *</Label>
                <Input
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="mt-1 text-sm"
                />
              </div>

              <div>
                <Label className="text-sm sm:text-base font-semibold">Amount Paid (₹) *</Label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={planDetails?.price}
                  className="mt-1 text-sm"
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must match the plan amount exactly
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded mt-3 sm:mt-4">
                <p className="text-xs sm:text-sm text-green-800">
                  <strong>✓ Next Steps:</strong> After submission, admin will verify your payment. 
                  You can proceed to create your event immediately. Your event will be activated after verification.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-3 sm:pt-4">
                <Button
                  onClick={() => navigate('/select-plan')}
                  variant="outline"
                  className="flex-1 text-sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitPayment}
                  disabled={submitting}
                  className="flex-1 bg-contribo-navy hover:bg-contribo-gold text-sm"
                >
                  {submitting ? 'Submitting...' : 'Submit & Continue'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base sm:text-xl">Common Questions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-xs sm:text-sm">
              <div>
                <p className="font-semibold text-gray-800">Q: Can I create my event before admin verification?</p>
                <p className="text-gray-600 mt-1">A: Yes! You can create your event immediately after submitting payment details. It will be activated once admin verifies your payment.</p>
              </div>
              <div>
                <p className="font-semibold text-gray-800">Q: Where can I find my transaction ID?</p>
                <p className="text-gray-600 mt-1">A: Open your UPI app → Go to transaction history → Find the payment → Copy the transaction ID/UTR number</p>
              </div>
              <div>
                <p className="font-semibold text-gray-800">Q: How long does verification take?</p>
                <p className="text-gray-600 mt-1">A: Usually within 24 hours. You'll be notified once verified.</p>
              </div>
            </div>
          </CardContent>
        </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
